import { z } from 'zod';
import { OUTCOME_CAUSE_INFO } from './session-outcome.js';
/**
 * Standing authority (Warren, 29 Oct 2026, item 1): what the platform may do with drivers' money on its own, within
 * limits he sets in Admin, Settings, Standing authority, and what always goes to a person. Shared by the Api (the policy
 * and the engine) and the Admin (the settings section and the Approvals page).
 *
 * Everything ships OFF (his ruling): the master switch, every rule and every email template. Nothing sends or moves
 * money on its own until he turns it on. Only the settings owner raises a limit, switches a rule on, loosens a check or
 * approves a template; any Super Admin may lower a limit, switch a rule off or tighten a check; every save is audited
 * with before and after (lib/standingAuthority/settings.ts). HARD_CEILINGS cap every value in code, so no settings write
 * can authorize unlimited money.
 */
export const SA_CURRENCIES = ['USD', 'CAD'];
/** The standing rules. */
export const SA_RULES = ['receipt', 'topup_refund', 'written_offer', 'service_make_good', 'small_waiver', 'display_repair'];
export const SA_RULE_LABELS = {
    receipt: 'Receipts',
    topup_refund: 'Wallet top-up refunds',
    written_offer: 'Anything offered in writing, once the driver says yes',
    service_make_good: 'Failed or short sessions caused by the charger or by us',
    small_waiver: 'Below-minimum and stranded session waivers',
    display_repair: 'Display-only data repairs',
};
/** The approver types (Warren, 29 Oct 2026): permission strings on User.permissions, granted only by the settings owner. */
export const APPROVER_TYPES = ['driver_money', 'host_payment', 'data', 'receipts'];
export const approverPermission = (t) => `approve:${t}`;
export const APPROVER_PERMISSIONS = APPROVER_TYPES.map(approverPermission);
export const APPROVER_TYPE_LABELS = {
    driver_money: 'Driver money: refunds, credits and waivers',
    host_payment: 'Host and partner payments',
    data: 'Data repairs',
    receipts: 'Receipts',
};
/** The emails and replies the platform could send on its own. Each needs Warren's approval of its text first. */
export const SA_TEMPLATES = ['make_good_email', 'receipt_email', 'receipt_reply', 'topup_refund_reply', 'offer_reply', 'holding_reply', 'safety_ack'];
export const SA_TEMPLATE_LABELS = {
    make_good_email: 'Make-good credit email (signed Warren Navarro, EV Initiative)',
    receipt_email: 'Receipt email with the PDF (signed EVII, Support.)',
    receipt_reply: 'Reply to a receipt request (signed EVII, Support.)',
    topup_refund_reply: 'Reply once a top-up refund is done (signed EVII, Support.)',
    offer_reply: 'Reply once an offered item is done (signed EVII, Support.)',
    holding_reply: 'Holding reply: a person will reply, no timeline',
    safety_ack: 'Safety acknowledgment',
};
/** What always goes to a person, in code. Settings can only add to it (extra_keywords), never remove from it. */
export const SA_EXCEPTIONS = [
    'over_limit', 'over_network_cap', 'dispute', 'host_or_partner_payment', 'legal', 'safety', 'angry', 'keyword', 'staff_account',
    'service_account', 'inactive_account', 'sender_not_account', 'sender_unverified', 'plan_not_ready', 'other_currency', 'tenant_not_operated',
    'fleet_billed', 'mirrored_v1', 'commissioning_test', 'residential_site', 'mail_before_start', 'person_handled_thread', 'second_approver_amount',
    'partly_spent', 'not_english', 'template_not_approved', 'rule_off', 'master_off', 'per_driver_limit', 'per_site_limit',
];
export const SA_EXCEPTION_LABELS = {
    over_limit: 'Above the limit for one action', over_network_cap: 'Past the network cap for the month', dispute: 'A dispute or chargeback',
    host_or_partner_payment: 'A host or partner payment', legal: 'Legal', safety: 'Safety', angry: 'An angry message', keyword: 'A word that always goes to a person',
    staff_account: 'An EVI staff account', service_account: 'A service account', inactive_account: 'An inactive account',
    sender_not_account: 'Not from the account email', sender_unverified: 'The sender could not be verified', plan_not_ready: 'The plan has refusals',
    other_currency: 'A currency other than USD or CAD', tenant_not_operated: 'A tenant EVI does not operate', fleet_billed: 'A fleet-billed session',
    mirrored_v1: 'A v1 session', commissioning_test: 'A commissioning test', residential_site: 'A residential site',
    mail_before_start: 'Mail from before standing authority was first switched on', person_handled_thread: 'A person has handled this thread',
    second_approver_amount: 'A session refund of US$100 or more', partly_spent: 'A partly spent top-up', not_english: 'Not in English',
    template_not_approved: 'Its email text is not approved yet', rule_off: 'This rule is off', master_off: 'Standing authority is off',
    per_driver_limit: 'Past the per-driver limit', per_site_limit: 'Past the per-site limit',
};
const money = z.object({ USD: z.number().int().min(0), CAD: z.number().int().min(0) }).strict();
const template = z.object({ approved: z.boolean(), approved_by: z.string().nullable(), approved_at: z.string().nullable() }).strict();
const owner = z.enum(['evi', 'site', 'charger', 'config', 'driver', 'unknown', 'none']);
export const StandingAuthoritySchema = z.object({
    /** Bumped on every save; recorded on every ledger row and card as the policy version. */
    version: z.number().int().min(0),
    /** The master switch. */
    enabled: z.boolean(),
    /** Written once, the first time the master switch turns on: nothing older acts on its own (no lookback). */
    started_at: z.string().nullable(),
    rules: z.object({
        receipt: z.object({ enabled: z.boolean(), max_per_account_per_day: z.number().int().min(0) }).strict(),
        topup_refund: z.object({ enabled: z.boolean(), max_age_days: z.number().int().min(0), per_driver_month: money }).strict(),
        written_offer: z.object({ enabled: z.boolean() }).strict(),
        service_make_good: z.object({
            enabled: z.boolean(), mode: z.enum(['shadow', 'live']), shadow_days: z.number().int().min(0),
            credit: money, expires_days: z.number().int().min(1), per_driver_30d: z.number().int().min(0), per_site_day: z.number().int().min(0),
            /** Short: under this many minutes or under this many kWh (the classifier's own thresholds). */
            short_minutes: z.number().int().min(0), short_kwh: z.number().min(0),
            /** A failed start then a successful session of at least retry_min_kwh at the same site within this many minutes is no incident. */
            retry_window_minutes: z.number().int().min(0), retry_min_kwh: z.number().min(0),
            /** "By the charger or by us": these cause owners, plus these causes, never these causes. */
            owners: z.array(owner), extra_causes: z.array(z.string()), never_causes: z.array(z.string()),
            refund_short: z.boolean(),
        }).strict(),
        small_waiver: z.object({ enabled: z.boolean(), per_session: money }).strict(),
        display_repair: z.object({ enabled: z.boolean(), max_rows: z.number().int().min(0) }).strict(),
    }).strict(),
    /** US cents per America/New_York calendar month. CAD counts at par; top-up refunds and accepted offers count; what a
     *  person approves adds to the total and is never blocked by it. */
    network_month_cap_usd_cents: z.number().int().min(0),
    approvals: z.object({
        /** Per approver type: the approver must also be a live Super Admin (so a backup can act while Warren is away). */
        needs_live_super_admin: z.object({ driver_money: z.boolean(), host_payment: z.boolean(), data: z.boolean(), receipts: z.boolean() }).strict(),
        expire_days: z.number().int().min(1),
        renotify_hours: z.array(z.number().int().min(1)),
    }).strict(),
    templates: z.object(Object.fromEntries(SA_TEMPLATES.map((t) => [t, template]))).strict(),
    /** Words that always send a message to a person, added to the code's own list (never subtracted from it). */
    extra_keywords: z.array(z.string().min(2).max(40)).max(100),
}).strict();
/** Warren's values (29 Oct 2026), every switch off. */
export const STANDING_AUTHORITY_SEED = {
    version: 0,
    enabled: false,
    started_at: null,
    rules: {
        receipt: { enabled: false, max_per_account_per_day: 3 },
        topup_refund: { enabled: false, max_age_days: 90, per_driver_month: { USD: 10_000, CAD: 10_000 } },
        written_offer: { enabled: false },
        service_make_good: {
            enabled: false, mode: 'shadow', shadow_days: 3, credit: { USD: 1_000, CAD: 1_000 }, expires_days: 90, per_driver_30d: 2, per_site_day: 10,
            short_minutes: 10, short_kwh: 1, retry_window_minutes: 30, retry_min_kwh: 1,
            owners: ['evi', 'charger', 'config'], extra_causes: ['charger_went_dark'], never_causes: ['charger_offline', 'timeout_no_plug', 'rfid_refused_unexplained', 'unclassified', 'session_unclassified'],
            refund_short: true,
        },
        small_waiver: { enabled: false, per_session: { USD: 2_500, CAD: 2_500 } },
        display_repair: { enabled: false, max_rows: 5_000 },
    },
    network_month_cap_usd_cents: 50_000,
    approvals: { needs_live_super_admin: { driver_money: false, host_payment: true, data: false, receipts: false }, expire_days: 7, renotify_hours: [24, 144] },
    templates: Object.fromEntries(SA_TEMPLATES.map((t) => [t, { approved: false, approved_by: null, approved_at: null }])),
    extra_keywords: [],
};
/** The most any setting may say, in code (Warren, 29 Oct 2026: network at most US$5,000 a month, one credit at most
 *  US$50). A save above one is refused. */
export const HARD_CEILINGS = {
    network_month_cap_usd_cents: 500_000,
    credit_cents: 5_000,
    topup_per_driver_month_cents: 50_000,
    waiver_per_session_cents: 10_000,
    display_repair_rows: 20_000,
    receipts_per_account_per_day: 10,
    topup_max_age_days: 180,
    make_good_per_driver_30d: 10,
    make_good_per_site_day: 50,
    make_good_expires_days: 365,
    shadow_days: 30,
    card_expire_days: 14,
};
/** Every ceiling a value breaks, as readable lines; [] when within them. */
export function ceilingBreaches(s) {
    const out = [];
    const over = (v, max, what) => { if (v > max)
        out.push(`${what} is at most ${max}`); };
    const c = HARD_CEILINGS;
    over(s.network_month_cap_usd_cents, c.network_month_cap_usd_cents, 'The network cap (cents)');
    for (const cur of SA_CURRENCIES) {
        over(s.rules.service_make_good.credit[cur], c.credit_cents, `One ${cur} credit (cents)`);
        over(s.rules.topup_refund.per_driver_month[cur], c.topup_per_driver_month_cents, `${cur} top-up refunds per driver per month (cents)`);
        over(s.rules.small_waiver.per_session[cur], c.waiver_per_session_cents, `A ${cur} waiver per session (cents)`);
    }
    over(s.rules.display_repair.max_rows, c.display_repair_rows, 'Rows per repair');
    over(s.rules.receipt.max_per_account_per_day, c.receipts_per_account_per_day, 'Receipts per account per day');
    over(s.rules.topup_refund.max_age_days, c.topup_max_age_days, 'Top-up age (days)');
    over(s.rules.service_make_good.per_driver_30d, c.make_good_per_driver_30d, 'Make-goods per driver per 30 days');
    over(s.rules.service_make_good.per_site_day, c.make_good_per_site_day, 'Make-goods per site per day');
    over(s.rules.service_make_good.expires_days, c.make_good_expires_days, 'Credit lifetime (days)');
    over(s.rules.service_make_good.shadow_days, c.shadow_days, 'Shadow days');
    over(s.approvals.expire_days, c.card_expire_days, 'Card lifetime (days)');
    return out;
}
/**
 * The changes in a save that loosen the standing authority: only the settings owner may make them. Everything else
 * (lowering a limit, switching off, tightening a check, withdrawing a template's approval) any Super Admin may save.
 * Returned as readable lines, [] when the save only tightens or changes nothing.
 */
export function looseningChanges(before, after) {
    const out = [];
    const up = (a, b, what) => { if (b > a)
        out.push(`raises ${what}`); };
    const on = (a, b, what) => { if (!a && b)
        out.push(`switches on ${what}`); };
    const grows = (a, b, what) => { if (b.some((x) => !a.includes(x)))
        out.push(`widens ${what}`); };
    const shrinks = (a, b, what) => { if (a.some((x) => !b.includes(x)))
        out.push(`narrows ${what}`); };
    on(before.enabled, after.enabled, 'standing authority');
    const r0 = before.rules, r1 = after.rules;
    for (const rule of SA_RULES)
        on(r0[rule].enabled, r1[rule].enabled, `the ${SA_RULE_LABELS[rule].toLowerCase()} rule`);
    up(r0.receipt.max_per_account_per_day, r1.receipt.max_per_account_per_day, 'receipts per account per day');
    up(r0.topup_refund.max_age_days, r1.topup_refund.max_age_days, 'the top-up age');
    const m0 = r0.service_make_good, m1 = r1.service_make_good;
    if (m0.mode === 'shadow' && m1.mode === 'live')
        out.push('takes make-goods live');
    // Fewer shadow days takes the rule live sooner.
    if (m1.shadow_days < m0.shadow_days)
        out.push('shortens the shadow days');
    up(m0.expires_days, m1.expires_days, 'the credit lifetime');
    up(m0.per_driver_30d, m1.per_driver_30d, 'make-goods per driver per 30 days');
    up(m0.per_site_day, m1.per_site_day, 'make-goods per site per day');
    up(m0.short_minutes, m1.short_minutes, 'the short-session minutes');
    up(m0.short_kwh, m1.short_kwh, 'the short-session kWh');
    // A shorter retry window or a lower retry bar counts more failed starts as incidents.
    if (m1.retry_window_minutes < m0.retry_window_minutes)
        out.push('shortens the retry window');
    if (m1.retry_min_kwh < m0.retry_min_kwh)
        out.push('lowers the retry kWh');
    grows(m0.owners, m1.owners, 'the cause owners');
    grows(m0.extra_causes, m1.extra_causes, 'the extra causes');
    shrinks(m0.never_causes, m1.never_causes, 'the causes never credited');
    if (!m0.refund_short && m1.refund_short)
        out.push('switches on short-session refunds');
    for (const cur of SA_CURRENCIES) {
        up(m0.credit[cur], m1.credit[cur], `the ${cur} credit`);
        up(r0.topup_refund.per_driver_month[cur], r1.topup_refund.per_driver_month[cur], `the ${cur} top-up refund limit`);
        up(r0.small_waiver.per_session[cur], r1.small_waiver.per_session[cur], `the ${cur} waiver limit`);
    }
    up(r0.display_repair.max_rows, r1.display_repair.max_rows, 'the repair row limit');
    up(before.network_month_cap_usd_cents, after.network_month_cap_usd_cents, 'the network cap');
    for (const t of APPROVER_TYPES) {
        if (before.approvals.needs_live_super_admin[t] && !after.approvals.needs_live_super_admin[t])
            out.push(`lets a non-Super Admin approve ${APPROVER_TYPE_LABELS[t].toLowerCase()}`);
    }
    up(before.approvals.expire_days, after.approvals.expire_days, 'the card lifetime');
    for (const t of SA_TEMPLATES) {
        if (!before.templates[t].approved && after.templates[t].approved)
            out.push(`approves the ${SA_TEMPLATE_LABELS[t].toLowerCase()}`);
    }
    shrinks(before.extra_keywords, after.extra_keywords, 'the words that go to a person');
    return out;
}
/** "By the charger or by us" (Warren, 29 Oct 2026): a failure whose cause owner is listed, or a listed extra cause,
 *  never a cause on the never list. */
export function makeGoodEligibleCause(cause, rule) {
    const info = OUTCOME_CAUSE_INFO[cause];
    if (!info || info.tally !== 'failure')
        return false;
    if (rule.never_causes.includes(cause))
        return false;
    return rule.owners.includes(info.owner) || rule.extra_causes.includes(cause);
}
/** US$ and C$ at par against the network cap; any other currency has no budget line. */
export const parCents = (cents, currency) => (currency === 'USD' || currency === 'CAD' ? cents : null);
/** The America/New_York calendar month (YYYY-MM) of a moment: the network cap's month. */
export function easternMonth(at) {
    const p = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit' }).formatToParts(at);
    return `${p.find((x) => x.type === 'year').value}-${p.find((x) => x.type === 'month').value}`;
}
/** The America/New_York calendar day (YYYY-MM-DD) of a moment. */
export function easternDay(at) {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).format(at);
}
//# sourceMappingURL=standing-authority.js.map